import React, { useState, useEffect } from 'react';

import macrobenthosImg from 'img/icon/macrobenthos.svg';

import { commonOptions, macrobenthosList } from 'data/eco-environmental-survey';

import LeafletMap from 'components/EcoEnvironmentalSurvey/LeafletMap/Content';
import Graph from 'components/EcoEnvironmentalSurvey/Graph';
import Intro from 'components/Intro';
import Tab from 'components/Tab';

import { useCustomContext } from 'utils/context';
import { useMacrobenthosContext } from 'context/MacrobenthosContext';
import Item from './Item';

const EMPTY_SPECIES_KEY = '__empty_species__';
const EMPTY_SPECIES_TITLE = '未觀測到物種';

const Content = () => {
  const { site } = useCustomContext();
  const [series, setSeries] = useState([]);
  const [dimensions, setDimensions] = useState(['year']);
  const { graphData, setGraphData, siteMacrobenthosSeries } =
    useMacrobenthosContext();

  const isNoSite = site === '';
  const isFetchingData = site !== '' && graphData.length === 0;

  const pageData = {
    title: '底棲生物',
    icon: macrobenthosImg,
    page: 'macrobenthos',
    tabs: [
      {
        id: 1,
        title: '人工觀測',
        content: (
          <>
            資料集以及調查方法可至
            {' '}
            <a
              href="https://data.depositar.io/en/dataset/fy_crab"
              className="e-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              depositar
            </a>
            {' '}
            頁面查看。
          </>
        ),
      },
      {
        id: 2,
        title: '自動監測',
        content: (
          <>
            資料集以及調查方法可至
            {' '}
            <a
              href="https://data.depositar.io/en/dataset/fy_crab"
              className="e-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              depositar
            </a>
            {' '}
            頁面查看。
          </>
        ),
      },
    ],
    url: 'crab',
  };

  const option = {
    ...commonOptions,
    dataZoom: {
      start: 0,
      end: 100,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(1, 116, 187, 0.8)',
      formatter: (params) => {
        let title = `${params[0].name}<br/>`;
        params.forEach((v) => {
          const speciesKey = v.seriesId;
          if (
            v.value[speciesKey] > 0 ||
            (speciesKey === EMPTY_SPECIES_KEY &&
              Object.keys(v.value).some(
                (key) => key !== 'year' && v.value[key] > 0
              ) === false)
          ) {
            title += `
              <div class="d-flex align-items-center">
                <div class="c-tooltip__legend" style="background-color: ${v.color}"></div>
                <div class="c-tooltip__row">
                  <div class="c-tooltip__column">
                  ${v.seriesName}
                  </div>
                  <div class="c-tooltip__column">
                  ${v.value[speciesKey]}
                  </div>
                </div>
              </div>
              `;
          }
        });
        return title;
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
    },
    yAxis: {
      name: '隻次/3平方公尺',
      type: 'value',
    },
    series: series,
    dataset: {
      dimensions,
      source: graphData,
    },
    toolbox: {
      feature: {
        ...commonOptions.toolbox.feature,
        saveAsImage: {
          show: false,
        },
      },
    },
  };

  const getSeries = (speciesTitleMap) => {
    const result = Object.entries(speciesTitleMap).map(([id, title]) => {
      return {
        id,
        type: 'bar',
        stack: 'Total',
        name: title,
        encode: {
          x: 'year',
          y: id,
        },
      };
    });
    setSeries([...result]);
  };

  const handleRawData = async () => {
    const speciesTitleMap = {};

    siteMacrobenthosSeries.forEach((item) => {
      Object.assign(speciesTitleMap, item.speciesTitles);
    });

    const hasEmptySpeciesSurvey = siteMacrobenthosSeries.some(
      (item) => Object.keys(item.species || {}).length === 0
    );
    if (hasEmptySpeciesSurvey) {
      speciesTitleMap[EMPTY_SPECIES_KEY] = EMPTY_SPECIES_TITLE;
    }

    const speciesKeys = Object.keys(speciesTitleMap);
    const processedData = siteMacrobenthosSeries.map((item) => {
      const { year, month, species } = item;
      const handleMonth = (month) => {
        if (month < 10) {
          return `0${month}`;
        } else {
          return month;
        }
      };

      const emptySpecies = speciesKeys.reduce((target, key) => {
        target[key] = 0;
        return target;
      }, {});

      return {
        year: `${year}-${handleMonth(month)}`,
        ...emptySpecies,
        ...(Object.keys(species || {}).length === 0
          ? { [EMPTY_SPECIES_KEY]: 0 }
          : {}),
        ...species,
      };
    });
    setDimensions(['year', ...speciesKeys]);
    setGraphData([...processedData]);
    getSeries(speciesTitleMap);
  };

  useEffect(() => {
    if (!isNoSite) {
      handleRawData();
    } else {
      setDimensions(['year']);
      setGraphData([]);
      setSeries([]);
    }
  }, [site, siteMacrobenthosSeries]);

  return (
    <>
      <article className="l-eco-environmental-survey">
        <section className="l-eco-environmental-survey__title u-section">
          <Intro data={pageData} />
          <Tab data={pageData} />
        </section>
        <section className="l-eco-environmental-survey__site u-section">
          <LeafletMap />
          {!isNoSite && (
            <>
              {!isFetchingData ? (
                <Graph
                  pageData={pageData}
                  graphData={graphData}
                  staticOption={option}
                  isFetchingData={isFetchingData}
                />
              ) : (
                <div className="c-placeholder l-eco-environmental-survey__placeholder">
                  圖表加載中...
                </div>
              )}
            </>
          )}
        </section>
        <section className="l-macrobenthos u-section" id="data">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="c-heading">
                <h2 className="c-title">底棲生物</h2>
              </div>
              <div className="l-macrobenthos__content u-section">
                <div className="row gy-4">
                  {macrobenthosList.map((v) => {
                    return <Item key={v.id} data={v} />;
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default Content;
